import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
  HarnessContext,
} from '@learnforge/demo-render/harness';
import type { FigureElement, FigureHandle, ReadoutHandle } from '@learnforge/demo-render';
import type { CompassToolsHandle, SnapPoint } from '@learnforge/demo-render/geometry';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import {
  add,
  dist,
  normalize,
  scale,
  sub,
  addObject,
  eraseObject,
  validateConstruction,
  scriptedSteps,
  type ConstructionObject,
  type ConstructionTarget,
  incenter as triIncenter,
  circumcenter as triCircumcenter,
  tangentPointsFromExternal,
  regularPolygonVertices,
  degToRad,
} from '@learnforge/demo-math/geometry';
import { CONSTRUCTION_PRESETS, type ConstructionPreset } from '../data/construction-presets';

// ============================================================================
// Constants
// ============================================================================

const SHOW_ME_INTERVAL_MS = 800;
const VALIDATION_EPS = 1e-3;

// ============================================================================
// Pure state model (exported for tests)
// ============================================================================

export interface ConstructionState {
  /** Objects provided at start; cannot be erased by the user. */
  given: readonly ConstructionObject[];
  /** All objects including given + drawn + minted intersections. */
  objects: readonly ConstructionObject[];
  /** History of user-drawn object ids (for undo). Each entry is the id of a
   *  directly drawn object (compass or straightedge); erasing it cascades. */
  history: readonly string[];
}

export function makeInitialState(given: readonly ConstructionObject[]): ConstructionState {
  return { given, objects: [...given], history: [] };
}

/**
 * Add an object (drawn by the user) and mint all new intersections.
 * Returns the updated state.
 */
export function reduceObjectDrawn(
  state: ConstructionState,
  newObj: ConstructionObject,
): ConstructionState {
  const nextObjects = addObject(state.objects, newObj);
  return {
    ...state,
    objects: nextObjects,
    history: [...state.history, newObj.id],
  };
}

/**
 * Undo: remove the last drawn object (and its intersection dependents).
 */
export function reduceUndo(state: ConstructionState): ConstructionState {
  if (state.history.length === 0) return state;
  const lastId = state.history[state.history.length - 1]!;
  const nextObjects = eraseObject(state.objects, lastId);
  return {
    ...state,
    objects: nextObjects,
    history: state.history.slice(0, -1),
  };
}

/**
 * Erase a named object and its dependents.
 * Given objects are protected — returns state unchanged if id is given.
 */
export function reduceErase(state: ConstructionState, id: string): ConstructionState {
  const isGiven = state.given.some((o) => o.id === id);
  if (isGiven) return state;

  const nextObjects = eraseObject(state.objects, id);
  // Remove erased ids from history (the erased obj and any dependents that were drawn)
  const surviving = new Set(nextObjects.map((o) => o.id));
  const nextHistory = state.history.filter((hid) => surviving.has(hid));
  return {
    ...state,
    objects: nextObjects,
    history: nextHistory,
  };
}

// ============================================================================
// Playback model
// ============================================================================

export interface PlaybackState {
  steps: ReturnType<typeof scriptedSteps>;
  stepIndex: number; // next step to execute (0 = not started)
  running: boolean;
}

export function makePlayback(target: ConstructionTarget): PlaybackState {
  return { steps: scriptedSteps(target), stepIndex: 0, running: false };
}

export function playbackStepLabel(pb: PlaybackState, t: (k: GeometryKey) => string): string {
  const idx = pb.stepIndex - 1; // last executed step
  if (idx < 0 || idx >= pb.steps.length) return '';
  const step = pb.steps[idx]!;
  return t(step.noteKey as GeometryKey);
}

// ============================================================================
// Rendering helpers
// ============================================================================

function pointsToSnapPoints(objects: readonly ConstructionObject[]): SnapPoint[] {
  return objects
    .filter((o): o is Extract<ConstructionObject, { kind: 'point' }> => o.kind === 'point')
    .map((o) => ({ id: o.id, at: o.at }));
}

function buildFigureElements(
  state: ConstructionState,
  preset: ConstructionPreset,
  showGhost: boolean,
  goalMet: boolean,
): FigureElement[] {
  const elems: FigureElement[] = [];
  const givenIds = new Set(state.given.map((o) => o.id));

  for (const obj of state.objects) {
    if (obj.kind === 'point') {
      const isGiven = givenIds.has(obj.id);
      elems.push({
        kind: 'point',
        id: `pt-${obj.id}`,
        at: obj.at,
        style: { className: isGiven ? 'geo-given-pt' : 'geo-drawn-pt', radius: 4 },
      });
    } else if (obj.kind === 'circle') {
      const center = state.objects.find((o) => o.id === obj.center);
      const through = state.objects.find((o) => o.id === obj.through);
      if (center && center.kind === 'point' && through && through.kind === 'point') {
        const isGiven = givenIds.has(obj.id);
        elems.push({
          kind: 'circle',
          id: `circ-${obj.id}`,
          center: `pt-${obj.center}`,
          through: `pt-${obj.through}`,
          style: { className: isGiven ? 'geo-given' : 'geo-drawn' },
        });
      }
    } else if (obj.kind === 'line') {
      const p1 = state.objects.find((o) => o.id === obj.through[0]);
      const p2 = state.objects.find((o) => o.id === obj.through[1]);
      if (p1 && p1.kind === 'point' && p2 && p2.kind === 'point') {
        const isGiven = givenIds.has(obj.id);
        elems.push({
          kind: 'line',
          id: `line-${obj.id}`,
          through: [`pt-${obj.through[0]}`, `pt-${obj.through[1]}`],
          style: { className: isGiven ? 'geo-given' : 'geo-drawn' },
        });
      }
    }
  }

  // Goal ghost
  if (showGhost) {
    const ghostElems = buildGoalGhost(preset, state.objects, goalMet);
    elems.push(...ghostElems);
  }

  return elems;
}

/**
 * Compute the analytic goal-ghost FigureElements for a given construction target.
 *
 * Returns a list of FigureElements (ghost points + strokes) that preview what
 * the finished construction should look like. The returned elements use
 * `className` which is either 'geo-ghost' (not yet met) or 'geo-glow' (met).
 *
 * Ghost-only point ids use the 'ghost-' prefix so they never collide with the
 * real construction point ids.
 *
 * Exported so it can be unit-tested independently.
 */
export function computeGoalGhost(
  target: ConstructionTarget,
  objects: readonly ConstructionObject[],
  className: string,
): FigureElement[] {
  const EXTEND = 120;

  /** Look up a point by id from the objects list. */
  function pt(id: string): Vec2 | null {
    const obj = objects.find((o) => o.id === id);
    return obj && obj.kind === 'point' ? obj.at : null;
  }

  /** Helper to emit a ghost point + a ghost ray from `origin` in direction `dir`. */
  function ghostRay(
    originId: string,
    dir: Vec2,
    endPointId: string,
    rayId: string,
  ): FigureElement[] {
    const origin = pt(originId);
    if (!origin) return [];
    const u = normalize(dir);
    if (u.x === 0 && u.y === 0) return [];
    const ghostEnd: Vec2 = add(origin, scale(u, EXTEND));
    return [
      { kind: 'point', id: endPointId, at: ghostEnd, style: { radius: 0 } },
      {
        kind: 'segment',
        id: rayId,
        from: `pt-${originId}`,
        to: endPointId,
        style: { className },
      },
    ];
  }

  /** Helper to emit a ghost infinite line through two ghost points. */
  function ghostLine(p: Vec2, dir: Vec2, idPrefix: string): FigureElement[] {
    const u = normalize(dir);
    if (u.x === 0 && u.y === 0) return [];
    const gp1: Vec2 = add(p, scale(u, EXTEND));
    const gp2: Vec2 = sub(p, scale(u, EXTEND));
    const id1 = `ghost-${idPrefix}-1`;
    const id2 = `ghost-${idPrefix}-2`;
    return [
      { kind: 'point', id: id1, at: gp1, style: { radius: 0 } },
      { kind: 'point', id: id2, at: gp2, style: { radius: 0 } },
      { kind: 'segment', id: `ghost-${idPrefix}-seg`, from: id1, to: id2, style: { className } },
    ];
  }

  switch (target.kind) {
    // ── 1. Perpendicular bisector ──────────────────────────────────────────
    case 'perpendicular-bisector': {
      const p1 = pt(target.of[0]);
      const p2 = pt(target.of[1]);
      if (!p1 || !p2) return [];
      const mid: Vec2 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      const along = sub(p2, p1);
      const perpDir: Vec2 = { x: -along.y, y: along.x };
      const u = normalize(perpDir);
      if (u.x === 0 && u.y === 0) return [];
      const ghostP1: Vec2 = add(mid, scale(u, EXTEND));
      const ghostP2: Vec2 = sub(mid, scale(u, EXTEND));
      return [
        { kind: 'point', id: 'ghost-mid', at: mid, style: { className, radius: 3 } },
        { kind: 'point', id: 'ghost-p1', at: ghostP1, style: { radius: 0 } },
        { kind: 'point', id: 'ghost-p2', at: ghostP2, style: { radius: 0 } },
        {
          kind: 'segment',
          id: 'ghost-bisector',
          from: 'ghost-p1',
          to: 'ghost-p2',
          style: { className },
        },
      ];
    }

    // ── 2. Angle bisector ──────────────────────────────────────────────────
    case 'angle-bisector': {
      const V = pt(target.at);
      const A = pt(target.arms[0]);
      const B = pt(target.arms[1]);
      if (!V || !A || !B) return [];
      const VA = normalize(sub(A, V));
      const VB = normalize(sub(B, V));
      const bisDir = add(VA, VB);
      return ghostRay(target.at, bisDir, 'ghost-bis-end', 'ghost-bisector');
    }

    // ── 3. Copy segment ────────────────────────────────────────────────────
    // Ghost: point on the ray at distance |AB| from R, plus the segment R→that point.
    case 'copy-segment': {
      const A = pt(target.from[0]);
      const B = pt(target.from[1]);
      const R = pt(target.ontoRayFrom);
      if (!A || !B || !R) return [];
      const segLen = dist(A, B);
      if (segLen < 1e-9) return [];
      // The ray direction is defined by looking for a line through ontoRayFrom in the given objects.
      // Fall back to the direction from A toward B if we can't find one.
      let rayDir: Vec2 = normalize(sub(B, A));
      const rayLine = objects.find(
        (o): o is Extract<ConstructionObject, { kind: 'line' }> =>
          o.kind === 'line' &&
          (o.through[0] === target.ontoRayFrom || o.through[1] === target.ontoRayFrom),
      );
      if (rayLine) {
        const other =
          rayLine.through[0] === target.ontoRayFrom ? rayLine.through[1] : rayLine.through[0];
        const Q = pt(other);
        if (Q) rayDir = normalize(sub(Q, R));
      }
      const targetPt: Vec2 = add(R, scale(rayDir, segLen));
      return [
        { kind: 'point', id: 'ghost-seg-end', at: targetPt, style: { className, radius: 4 } },
        {
          kind: 'segment',
          id: 'ghost-seg',
          from: `pt-${target.ontoRayFrom}`,
          to: 'ghost-seg-end',
          style: { className },
        },
      ];
    }

    // ── 4. Copy angle ──────────────────────────────────────────────────────
    // Ghost: a ray from R making angle ∠AVB against the base ray direction.
    case 'copy-angle': {
      const V = pt(target.at);
      const Aarm = pt(target.arms[0]);
      const Barm = pt(target.arms[1]);
      const R = pt(target.ontoRayFrom);
      if (!V || !Aarm || !Barm || !R) return [];
      // Determine base ray direction at R (first line through R in objects).
      let baseDir: Vec2 = { x: 1, y: 0 };
      const baseLine = objects.find(
        (o): o is Extract<ConstructionObject, { kind: 'line' }> =>
          o.kind === 'line' &&
          (o.through[0] === target.ontoRayFrom || o.through[1] === target.ontoRayFrom),
      );
      if (baseLine) {
        const otherId =
          baseLine.through[0] === target.ontoRayFrom ? baseLine.through[1] : baseLine.through[0];
        const Q = pt(otherId);
        if (Q) baseDir = normalize(sub(Q, R));
      }
      // Compute the angle ∠AVB (signed: rotation from arm[0] to arm[1]).
      const VA = normalize(sub(Aarm, V));
      const VB = normalize(sub(Barm, V));
      const cosTheta = Math.max(-1, Math.min(1, VA.x * VB.x + VA.y * VB.y));
      const sinTheta = VA.x * VB.y - VA.y * VB.x; // signed cross product
      const theta = Math.atan2(sinTheta, cosTheta);
      // Rotate baseDir by theta to get the ghost ray direction.
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      const ghostDir: Vec2 = {
        x: baseDir.x * cos - baseDir.y * sin,
        y: baseDir.x * sin + baseDir.y * cos,
      };
      return ghostRay(target.ontoRayFrom, ghostDir, 'ghost-angle-end', 'ghost-angle-ray');
    }

    // ── 5. Perpendicular at point ──────────────────────────────────────────
    case 'perpendicular-at-point': {
      const L1 = pt(target.line[0]);
      const L2 = pt(target.line[1]);
      const P = pt(target.at);
      if (!L1 || !L2 || !P) return [];
      const lineDir = sub(L2, L1);
      const perpDir: Vec2 = { x: -lineDir.y, y: lineDir.x };
      return ghostLine(P, perpDir, 'perp-at');
    }

    // ── 6. Perpendicular from point ────────────────────────────────────────
    case 'perpendicular-from-point': {
      const L1 = pt(target.line[0]);
      const L2 = pt(target.line[1]);
      const P = pt(target.from);
      if (!L1 || !L2 || !P) return [];
      const lineDir = sub(L2, L1);
      const perpDir: Vec2 = { x: -lineDir.y, y: lineDir.x };
      return ghostLine(P, perpDir, 'perp-from');
    }

    // ── 7. Inscribed polygon ───────────────────────────────────────────────
    case 'inscribed-polygon': {
      // Find the given circle by id.
      const circleObj = objects.find(
        (o): o is Extract<ConstructionObject, { kind: 'circle' }> =>
          o.kind === 'circle' && o.id === target.inCircle,
      );
      if (!circleObj) return [];
      const center = pt(circleObj.center);
      const throughPt = pt(circleObj.through);
      if (!center || !throughPt) return [];
      const r = dist(center, throughPt);
      if (r < 1e-9) return [];
      // Start angle aligned to the through-point so the ghost matches the preset's P0.
      const startAngle = Math.atan2(throughPt.y - center.y, throughPt.x - center.x);
      const vertices = regularPolygonVertices(target.n, r, center);
      // Rotate vertices so vertex 0 aligns with startAngle.
      const defaultAngle = 0; // regularPolygonVertices starts at angle 0
      const rotation = startAngle - defaultAngle;
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const rotated: Vec2[] = vertices.map((v) => {
        const dx = v.x - center.x;
        const dy = v.y - center.y;
        return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
      });
      const elems: FigureElement[] = [];
      for (let i = 0; i < target.n; i++) {
        const vId = `ghost-poly-v${i}`;
        elems.push({ kind: 'point', id: vId, at: rotated[i]!, style: { radius: 0 } });
      }
      for (let i = 0; i < target.n; i++) {
        const fromId = `ghost-poly-v${i}`;
        const toId = `ghost-poly-v${(i + 1) % target.n}`;
        elems.push({
          kind: 'segment',
          id: `ghost-poly-s${i}`,
          from: fromId,
          to: toId,
          style: { className },
        });
      }
      return elems;
    }

    // ── 8. Tangent from external point ────────────────────────────────────
    case 'tangent-from-point': {
      const circleObj = objects.find(
        (o): o is Extract<ConstructionObject, { kind: 'circle' }> =>
          o.kind === 'circle' && o.id === target.circle,
      );
      if (!circleObj) return [];
      const O = pt(circleObj.center);
      const throughPt = pt(circleObj.through);
      const P = pt(target.from);
      if (!O || !throughPt || !P) return [];
      const r = dist(O, throughPt);
      const tangentPts = tangentPointsFromExternal(O, r, P);
      if (tangentPts.length === 0) return [];
      const elems: FigureElement[] = [];
      tangentPts.forEach((tp, i) => {
        const tpId = `ghost-tangent-tp${i}`;
        const segId = `ghost-tangent-line${i}`;
        elems.push({ kind: 'point', id: tpId, at: tp, style: { className, radius: 3 } });
        elems.push({
          kind: 'segment',
          id: segId,
          from: `pt-${target.from}`,
          to: tpId,
          style: { className },
        });
      });
      return elems;
    }

    // ── 9. Incircle ────────────────────────────────────────────────────────
    case 'incircle': {
      const pA = pt(target.of[0]);
      const pB = pt(target.of[1]);
      const pC = pt(target.of[2]);
      if (!pA || !pB || !pC) return [];
      const incenterPt = triIncenter({ a: pA, b: pB, c: pC });
      // Inradius = perpendicular distance from incenter to side AB.
      const AB = sub(pB, pA);
      const ABlen = Math.sqrt(AB.x * AB.x + AB.y * AB.y);
      if (ABlen < 1e-9) return [];
      const AP = sub(incenterPt, pA);
      const inradius = Math.abs(AP.x * AB.y - AP.y * AB.x) / ABlen;
      if (inradius < 1e-9) return [];
      // Ghost circle: centre at incenter, through a point on the inradius.
      const perpU = normalize({ x: -AB.y, y: AB.x });
      const onCircle: Vec2 = add(incenterPt, scale(perpU, inradius));
      return [
        { kind: 'point', id: 'ghost-incenter', at: incenterPt, style: { className, radius: 3 } },
        { kind: 'point', id: 'ghost-incirc-through', at: onCircle, style: { radius: 0 } },
        {
          kind: 'circle',
          id: 'ghost-incircle',
          center: 'ghost-incenter',
          through: 'ghost-incirc-through',
          style: { className },
        },
      ];
    }

    // ── 10. Circumcircle ───────────────────────────────────────────────────
    case 'circumcircle': {
      const pA = pt(target.of[0]);
      const pB = pt(target.of[1]);
      const pC = pt(target.of[2]);
      if (!pA || !pB || !pC) return [];
      const ccenter = triCircumcenter({ a: pA, b: pB, c: pC });
      if (!ccenter) return [];
      const circumradius = dist(ccenter, pA);
      if (circumradius < 1e-9) return [];
      const onCircle: Vec2 = add(ccenter, { x: circumradius, y: 0 });
      return [
        { kind: 'point', id: 'ghost-circumcenter', at: ccenter, style: { className, radius: 3 } },
        { kind: 'point', id: 'ghost-circumcirc-through', at: onCircle, style: { radius: 0 } },
        {
          kind: 'circle',
          id: 'ghost-circumcircle',
          center: 'ghost-circumcenter',
          through: 'ghost-circumcirc-through',
          style: { className },
        },
      ];
    }

    // ── 11. Angle of measure ───────────────────────────────────────────────
    // Ghost: a ray from R making angle `deg` against the base ray.
    case 'angle-of-measure': {
      const R = pt(target.atRayOrigin);
      if (!R) return [];
      // Base ray direction from the first line through R.
      let baseDir: Vec2 = { x: 1, y: 0 };
      const baseLine = objects.find(
        (o): o is Extract<ConstructionObject, { kind: 'line' }> =>
          o.kind === 'line' &&
          (o.through[0] === target.atRayOrigin || o.through[1] === target.atRayOrigin),
      );
      if (baseLine) {
        const otherId =
          baseLine.through[0] === target.atRayOrigin ? baseLine.through[1] : baseLine.through[0];
        const Q = pt(otherId);
        if (Q) baseDir = normalize(sub(Q, R));
      }
      const theta = degToRad(target.deg);
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      const ghostDir: Vec2 = {
        x: baseDir.x * cos - baseDir.y * sin,
        y: baseDir.x * sin + baseDir.y * cos,
      };
      return ghostRay(target.atRayOrigin, ghostDir, 'ghost-angle-end', 'ghost-angle-ray');
    }

    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

function buildGoalGhost(
  preset: ConstructionPreset,
  objects: readonly ConstructionObject[],
  goalMet: boolean,
): FigureElement[] {
  const className = goalMet ? 'geo-glow' : 'geo-ghost';
  return computeGoalGhost(preset.target, objects, className);
}

// ============================================================================
// Harness State
// ============================================================================

interface ConstructionsState {
  presetId: string;
  construction: ConstructionState;
  showGhost: boolean;
  currentTool: 'compass' | 'straightedge' | 'eraser' | 'none';
  nextObjId: number;
}

function findPresetById(id: string, advanced: boolean): ConstructionPreset {
  const found = CONSTRUCTION_PRESETS.find((p) => p.id === id);
  if (found && (!found.advancedOnly || advanced)) return found;
  const defaultPreset = CONSTRUCTION_PRESETS.find((p) => !p.advancedOnly)!;
  return defaultPreset;
}

function freshId(state: ConstructionsState): { id: string; nextObjId: number } {
  const id = `drawn-${state.nextObjId + 1}`;
  return { id, nextObjId: state.nextObjId + 1 };
}

function defaultState(advanced: boolean, presetId?: string): ConstructionsState {
  const preset = findPresetById(
    presetId || CONSTRUCTION_PRESETS.find((p) => !p.advancedOnly)!.id,
    advanced,
  );
  return {
    presetId: preset.id,
    construction: makeInitialState(preset.given),
    showGhost: true,
    currentTool: 'compass',
    nextObjId: 0,
  };
}

// ============================================================================
// Experiment
// ============================================================================

function buildSpec(): ExperimentSpec<ConstructionsState, GeometryKey, GeometryContext> {
  let figure: FigureHandle;
  let readout: ReadoutHandle;
  let compass: CompassToolsHandle;
  let controllerRef: HarnessController<ConstructionsState>;
  let liveState: ConstructionsState;
  let showMeRafId: number | null = null;

  function stopShowMe(): void {
    if (showMeRafId !== null && controllerRef) {
      controllerRef.cancelRaf(showMeRafId);
      showMeRafId = null;
    }
  }

  function startShowMe(
    fromState: ConstructionsState,
    ctx: HarnessContext<GeometryKey, GeometryContext>,
  ): void {
    if (!controllerRef) return;
    stopShowMe();
    const preset = findPresetById(fromState.presetId, ctx.advanced);
    const steps = scriptedSteps(preset.target);
    controllerRef.setState((prev) => ({
      ...prev,
      nextObjId: 0,
      construction: makeInitialState(preset.given),
    }));
    let stepIndex = 0;
    let startTime: number | null = null;
    const STEP_MS = SHOW_ME_INTERVAL_MS;
    const frame = (now: number): void => {
      if (startTime === null) startTime = now;
      const due = Math.floor((now - startTime) / STEP_MS);
      while (stepIndex < steps.length && stepIndex < due) {
        const step = steps[stepIndex]!;
        const sid = `s${stepIndex + 1}`;
        const newObj: ConstructionObject =
          step.tool === 'compass'
            ? { kind: 'circle', id: sid, center: step.clicks[0], through: step.clicks[1] }
            : { kind: 'line', id: sid, through: [step.clicks[0], step.clicks[1]] };
        controllerRef.setState((prev) => ({
          ...prev,
          construction: {
            ...prev.construction,
            objects: addObject(prev.construction.objects, newObj),
            history: [...prev.construction.history, sid],
          },
        }));
        stepIndex += 1;
      }
      if (stepIndex < steps.length) {
        showMeRafId = controllerRef.raf(frame);
      } else {
        showMeRafId = null;
      }
    };
    showMeRafId = controllerRef.raf(frame);
  }

  return {
    labelKey: 'experiment.constructions',
    demoId: '2d-geometry',
    advancedKey: 'constructions',

    initialState(ctx) {
      return defaultState(ctx.advanced);
    },

    applyPreset(_state, presetId, ctx) {
      return defaultState(ctx.advanced, presetId);
    },

    setup(ctx, controller) {
      controllerRef = controller;
      figure = controller.createFigure();
      readout = controller.createReadout();
      compass = controller.createCompassTools({
        screenToWorld: (p) => p,
        worldToScreen: (p) => p,
        hitTest: (worldAt: Vec2): string | null => {
          const HIT_RADIUS = 12;
          let best: string | null = null;
          let bestDist = HIT_RADIUS;
          const st = liveState.construction;
          const givenIds = new Set(st.given.map((o) => o.id));
          for (const obj of st.objects) {
            if (givenIds.has(obj.id)) continue;
            if (obj.kind === 'point' && obj.source !== 'given') {
              const d = Math.sqrt((obj.at.x - worldAt.x) ** 2 + (obj.at.y - worldAt.y) ** 2);
              if (d < bestDist) {
                bestDist = d;
                best = obj.id;
              }
            }
          }
          return best;
        },
      });
      compass.on((evt) => {
        if (evt.type === 'objectDrawn') {
          controller.setState((prev) => {
            const { id, nextObjId } = freshId(prev);
            const newObj: ConstructionObject =
              evt.tool === 'compass'
                ? { kind: 'circle', id, center: evt.center, through: evt.through }
                : { kind: 'line', id, through: evt.through };
            return {
              ...prev,
              nextObjId,
              construction: reduceObjectDrawn(prev.construction, newObj),
            };
          });
        } else if (evt.type === 'objectErased') {
          controller.setState((prev) => ({
            ...prev,
            construction: reduceErase(prev.construction, evt.id),
          }));
        }
      });
      controller.registerCleanup(() => ctx.infoBadge.hide());
    },

    render(state, ctx) {
      liveState = state;
      const preset = findPresetById(state.presetId, ctx.advanced);
      const goalMet = validateConstruction(
        preset.target,
        state.construction.objects,
        VALIDATION_EPS,
      ).met;
      figure.update(buildFigureElements(state.construction, preset, state.showGhost, goalMet));
      compass.setTool(state.currentTool);
      compass.setSnapPoints(pointsToSnapPoints(state.construction.objects));
      if (goalMet) {
        ctx.infoBadge.show({ name: ctx.i18n.t('construct.readout.goal-met'), value: '✓' });
        readout.update([
          {
            id: 'goal',
            lhs: ctx.i18n.t('construct.readout.goal-met'),
            relation: '=',
            rhs: '✓',
            emphasis: 'invariant',
          },
        ]);
      } else {
        ctx.infoBadge.hide();
        readout.update([
          {
            id: 'goal',
            lhs: ctx.i18n.t('construct.readout.working'),
            relation: '=',
            rhs:
              ctx.i18n.t(('construct.target.' + preset.id.replace(/-(\d+)$/, '')) as GeometryKey) ??
              preset.id,
          },
        ]);
      }
    },

    panel(state, ctx): PanelRow<GeometryKey, ConstructionsState>[] {
      const rows: PanelRow<GeometryKey, ConstructionsState>[] = [];
      const filteredPresets = CONSTRUCTION_PRESETS.filter((p) => !p.advancedOnly || ctx.advanced);
      rows.push({
        kind: 'dropdown',
        id: 'construct-target',
        labelKey: 'construct.panel.target',
        options: filteredPresets.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => {
          stopShowMe();
          return defaultState(ctx.advanced, v);
        },
      });
      rows.push({
        kind: 'toggle',
        labelKey: 'construct.panel.show-ghost',
        value: state.showGhost,
        onChange: (v) => ({ ...state, showGhost: v }),
      });
      rows.push({
        kind: 'dropdown',
        id: 'construct-tool',
        labelKey: 'panel.tool',
        options: [
          { value: 'compass', labelKey: 'construct.tool.compass' },
          { value: 'straightedge', labelKey: 'construct.tool.straightedge' },
          { value: 'eraser', labelKey: 'construct.tool.eraser' },
        ],
        value: state.currentTool === 'none' ? 'compass' : state.currentTool,
        onChange: (v) => ({ ...state, currentTool: v as 'compass' | 'straightedge' | 'eraser' }),
      });
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });
      return rows;
    },

    actions: (_state, ctx): ActionBarSpec<GeometryKey, ConstructionsState> => ({
      // Each handler uses the LIVE state argument the harness passes (not a
      // closed-over snapshot) so an in-flight Show Me frame can't leave them
      // operating on stale state.
      onReset: (s) => {
        stopShowMe();
        return defaultState(ctx.advanced, s.presetId);
      },
      onRandomise: (s) => {
        stopShowMe();
        const available = CONSTRUCTION_PRESETS.filter((p) => !p.advancedOnly || ctx.advanced);
        const idx = Math.floor(ctx.prng() * available.length);
        const picked = available[idx];
        return picked ? defaultState(ctx.advanced, picked.id) : s;
      },
      undo: {
        onUndo: (s) => {
          stopShowMe();
          return { ...s, construction: reduceUndo(s.construction) };
        },
      },
      showMe: {
        onShowMe: (s) => {
          startShowMe(s, ctx);
          return;
        },
      },
      eraser: {
        onErase: (s) => ({
          ...s,
          currentTool: s.currentTool === 'eraser' ? 'compass' : 'eraser',
        }),
      },
    }),

    applyShotState(state, shotId) {
      if (shotId === 'constructions') {
        const preset = CONSTRUCTION_PRESETS.find((p) => p.id === 'perpendicular-bisector')!;
        const steps = scriptedSteps(preset.target);
        let construction = makeInitialState(preset.given);
        for (let i = 0; i < steps.length; i++) {
          const step = steps[i]!;
          const sid = `s${i + 1}`;
          const obj: ConstructionObject =
            step.tool === 'compass'
              ? { kind: 'circle', id: sid, center: step.clicks[0], through: step.clicks[1] }
              : { kind: 'line', id: sid, through: [step.clicks[0], step.clicks[1]] };
          construction = {
            ...construction,
            objects: addObject(construction.objects, obj),
            history: [...construction.history, sid],
          };
        }
        return {
          ...state,
          presetId: 'perpendicular-bisector',
          showGhost: true,
          currentTool: 'none',
          construction,
          nextObjId: steps.length,
        };
      }
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
